import { Box, Button } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { CHART_BLUE } from '../../theme/theme-colors';
import type { Series } from '../Dashboard/dashboardTypes';
import { chartSeriesButtonSx, chartSeriesToolbarSx } from './chartSeriesButtonSx';
import type { ChartSeriesLabelOptions } from './chartSeriesLabel';
import { chartSeriesButtonI18nKey, chartSeriesLabel, chartSeriesUsesPlainLabel } from './chartSeriesLabel';

type ChartSeriesToggleButtonsProps = {
  series: readonly Pick<Series, 'dataKey' | 'color'>[];
  activeDataKeys: ReadonlySet<string>;
  chartSeriesLabelOptions?: ChartSeriesLabelOptions;
  onToggle: (dataKey: string) => void;
};

/** Series picker row under line/bar charts; wraps at four buttons per row and scales down when crowded. */
export function ChartSeriesToggleButtons({ series, activeDataKeys, chartSeriesLabelOptions, onToggle }: ChartSeriesToggleButtonsProps) {
  const { t } = useTranslation();
  const count = series.length;

  if (count <= 1) return null;

  return (
    <Box sx={chartSeriesToolbarSx(count)}>
      {series.map((item) => {
        const label = chartSeriesLabel(t, item.dataKey, chartSeriesLabelOptions);
        const plainLabel = chartSeriesUsesPlainLabel(item.dataKey, chartSeriesLabelOptions);
        const color = item.color ?? CHART_BLUE;
        const isActive = activeDataKeys.has(item.dataKey);

        return (
          <Button
            key={item.dataKey}
            variant="contained"
            onClick={() => onToggle(item.dataKey)}
            size="small"
            aria-pressed={isActive}
            sx={chartSeriesButtonSx(color, count, isActive)}
          >
            {plainLabel ? label : <Trans i18nKey={chartSeriesButtonI18nKey(item.dataKey)}>{label}</Trans>}
          </Button>
        );
      })}
    </Box>
  );
}
